import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  Text,
  Tooltip
} from 'pcln-design-system'

import DataProvider from './DataProvider'
import Display from './Display'
import Control from './Control'
import Popover from '../Popover'
import { callAll } from '../../utils/ramda-helpers'

const ControlPanel = styled(Card)`
  z-index: 4;
  user-select: none;
`

const errorTooltipBaseStyles = {
  zIndex: 1,
  right: true,
  bg: 'red',
  color: 'white'
}

const TravelerSelection = props => {
  const {
    travelerSelectionContent,
    isLargeInput = false,
    isUsingRoomControl,
    isUsingAdultControl,
    isUsingChildControl,
    isUsingYouthControl,
    isUsingLapInfantControl,
    isUsingChildAgeControl,
    isUsingControlLabel,
    isUsingErrorTooltip,
    isErrorTooltipTop,
    disclaimer,
    initNumRooms,
    initNumAdults,
    initNumYouths = 0,
    initNumChildren = 0,
    initNumInfants = 0,
    initDisplayLabel,
    initChildAges = [],
    isMobile,
    minAdultCount,
    onChange,
    handleInputOnFocus,
    error,
    maxWidth,
    onDoneButtonClick,
    displayProps = {}
  } = props

  const TRAVELER_TYPES = props.travelerSelectionContent.TRAVELER_TYPES

  const errorTooltipTopOrBottom = isErrorTooltipTop
    ? { top: true }
    : { bottom: true }

  const errorTooltipProps = {
    ...errorTooltipTopOrBottom,
    ...errorTooltipBaseStyles
  }

  return (
    <DataProvider
      travelerSelectionContent={travelerSelectionContent}
      isUsingRoomControl={isUsingRoomControl}
      initNumRooms={initNumRooms}
      initNumAdults={initNumAdults}
      initNumYouths={initNumYouths}
      initNumChildren={initNumChildren}
      initNumInfants={initNumInfants}
      initChildAges={initChildAges}
      minAdultCount={minAdultCount}
      onChange={data => onChange && onChange(data)}
      error={error}
      render={data => {
        const {
          state,
          toggleIsControlPanelOpenState,
          updateChildAge,
          updateTravelerCount
        } = data

        const { travelers, isControlPanelOpen, disabledIcons } = state

        const {
          roomCount,
          adultCount,
          youthCount,
          childCount,
          infantCount,
          childAges,
          displayLabel
        } = travelers

        const parseControls = () => {
          const controls = []

          // TODO: Currently order of appearance for controls are fixed.
          // It can scale to a prop support for order of appearance.
          if (isUsingRoomControl) {
            controls.push({
              travelerType: TRAVELER_TYPES.ROOMS,
              travelerCount: roomCount
            })
          }

          if (isUsingAdultControl) {
            controls.push({
              travelerType: TRAVELER_TYPES.ADULTS,
              travelerCount: adultCount
            })
          }

          if (isUsingYouthControl) {
            controls.push({
              travelerType: TRAVELER_TYPES.YOUTHS,
              travelerCount: youthCount
            })
          }

          if (isUsingChildControl) {
            controls.push({
              travelerType: TRAVELER_TYPES.CHILDREN,
              travelerCount: childCount
            })
          }

          if (isUsingLapInfantControl) {
            controls.push({
              travelerType: TRAVELER_TYPES.LAP_INFANTS,
              travelerCount: infantCount
            })
          }

          if (isUsingChildAgeControl && childCount > 0) {
            for (let i = 1; i <= childCount; i++) {
              const currentAgeObject = childAges[i - 1]
              const childAge = currentAgeObject || -1

              controls.push({
                travelerType: TRAVELER_TYPES.CHILD_AGE,
                childIndex: i,
                childAge,
                updateChildAge
              })
            }
          }

          return controls.map((controlProps, index, controls) => (
            <Box key={`Control-Box-${index}`}>
              <Control
                {...Object.assign(controlProps, {
                  toggleIsControlPanelOpenState: toggleIsControlPanelOpenState,
                  updateTravelerCount: updateTravelerCount,
                  disabledIcons: disabledIcons,
                  isUsingControlLabel
                })}
              />
              {index + 1 !== controls.length && (
                <Divider
                  key={`Divider-${index}`}
                  mx={3}
                  my={0}
                  color='background.light'
                />
              )}
            </Box>
          ))
        }

        const controlPanelRef = React.createRef()
        // Use the "initDisplayLabel" as back-up label to support init page render not parsing "displayLabel"
        const infoLabel = displayLabel || initDisplayLabel

        // TODO: revisit the parsing code of the info label string to avoid any whitespace that needs trimming
        return (
          <Container maxWidth={maxWidth}>
            <Popover
              key='traveler-selection'
              data-traveler-selection='true'
              open={isControlPanelOpen}
              width={1}
              zIndex={isControlPanelOpen ? 2 : 0}
              color='background.lightest'
            >
              {!isControlPanelOpen &&
                isUsingErrorTooltip &&
                isErrorTooltipTop &&
                error && <Tooltip {...errorTooltipProps}>{error}</Tooltip>}
              <Display
                isLargeInput={isLargeInput}
                value={infoLabel.trim()}
                error={error}
                isControlPanelOpen={isControlPanelOpen}
                toggleIsControlPanelOpenState={toggleIsControlPanelOpenState}
                controlPanelRef={controlPanelRef}
                {...displayProps}
                handleClick={() => {
                  if (toggleIsControlPanelOpenState) {
                    toggleIsControlPanelOpenState()
                  }
                }}
                handleFocus={
                  () => {
                    if (handleInputOnFocus) {
                      handleInputOnFocus()
                    }
                  }
                }
              />
              {!isControlPanelOpen &&
                isUsingErrorTooltip &&
                !isErrorTooltipTop &&
                error && <Tooltip {...errorTooltipProps}>{error}</Tooltip>}
              <div style={{ display: (isControlPanelOpen ? '' : 'none') }}>
                <Popover.Body
                  color={isMobile ? 'background.light' : 'transparent'}
                  mt={[0, null, 2]}
                  mr={[0, null, -3, 0]}
                  openState={isControlPanelOpen}
                >
                  <ControlPanel ref={controlPanelRef}
                    color='background.lightest'
                    boxShadowSize='md'
                    width={[1, null, null, 280]}
                    id='traveler-selection-control-panel'
                  >
                    {parseControls()}
                    <Button
                      type='button'
                      id='traveler-selection-done-button'
                      title='traveler-selection-done-button'
                      onClick={callAll(() => toggleIsControlPanelOpenState(), onDoneButtonClick)}
                      style={{
                        display: 'block',
                        margin: '0 auto 16px auto',
                        width: '85%'
                      }}
                    >
                      Done
                    </Button>
                    {disclaimer && (
                      <Text fontSize={0} m={3}>
                        {disclaimer}
                      </Text>
                    )}
                  </ControlPanel>
                </Popover.Body>
              </div>
              {!isControlPanelOpen &&
                !isUsingErrorTooltip &&
                error && (
                <Text fontSize={0} mt={3} color='red'>
                  {error}
                </Text>
              )}
            </Popover>
          </Container>
        )
      }}
    />
  )
}

TravelerSelection.defaultProps = {
  travelerSelectionContent: {
    TRAVELER_TYPES: {
      ROOMS: 'ROOMS',
      ADULTS: 'ADULTS',
      YOUTHS: 'YOUTHS',
      CHILDREN: 'CHILDREN',
      LAP_INFANTS: 'LAP_INFANTS',
      CHILD_AGE: 'CHILD_AGE'
    },
    TRAVELER_LABELS: {
      ROOMS: 'Rooms',
      ADULTS: 'Adults',
      YOUTHS: 'Youths',
      CHILDREN: 'Children',
      LAP_INFANTS: 'Lap Infants'
    },
    TRAVELER_SUBTEXTS: {
      YOUTHS: '(ages 15-17)',
      CHILDREN: '(ages 2-14)',
      LAP_INFANTS: '(under 2)'
    },
    BUTTON_TYPE: {
      MINUS: 'MINUS',
      PLUS: 'PLUS'
    },
    AIR_DISCLAIMER:
      'You may only book a flight for up to 8 ticketed passengers.',
    VP_DISCLAIMER: 'You may only book a package for up to 8 travelers.'
  },
  isUsingRoomControl: false,
  isUsingAdultControl: true,
  isUsingYouthControl: false,
  isUsingChildControl: false,
  isUsingLapInfantControl: false,
  isUsingChildAgeControl: false,
  isUsingControlLabel: true,
  isUsingErrorTooltip: false,
  isErrorTooltipTop: false,
  disclaimer: null,
  initNumRooms: 0,
  initNumAdults: 0,
  initNumYouths: 0,
  initNumChildren: 0,
  initNumInfants: 0,
  initDisplayLabel: '',
  minAdultCount: 0,
  maxWidth: 768,
  onDoneButtonClick: () => {}
}

TravelerSelection.propTypes = {
  travelerSelectionContent: PropTypes.object.isRequired,
  isLargeInput: PropTypes.bool, // TODO: HP_LP_VP_LARGER_INPUTS clean up
  isUsingRoomControl: PropTypes.bool,
  isUsingAdultControl: PropTypes.bool,
  isUsingYouthControl: PropTypes.bool,
  isUsingChildControl: PropTypes.bool,
  isUsingLapInfantControl: PropTypes.bool,
  isUsingChildAgeControl: PropTypes.bool,
  isUsingControlLabel: PropTypes.bool,
  isUsingErrorTooltip: PropTypes.bool,
  disclaimer: PropTypes.string,
  isErrorTooltipTop: PropTypes.bool,
  initNumRooms: PropTypes.number,
  initNumAdults: PropTypes.number,
  initNumYouths: PropTypes.number,
  initNumChildren: PropTypes.number,
  initNumInfants: PropTypes.number,
  initDisplayLabel: PropTypes.string,
  initChildAges: PropTypes.object,
  isMobile: PropTypes.boolean,
  minAdultCount: PropTypes.number,
  onChange: PropTypes.func,
  handleInputOnFocus: PropTypes.func,
  error: PropTypes.string,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onDoneButtonClick: PropTypes.func,
  displayProps: PropTypes.object
}

export default TravelerSelection
